<template>
  <div class="payment-receipt-detail-page">
    <PaymentReceiptsPageShell
      :title="headerTitle"
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #left>
        <div class="payment-receipt-detail-header-left">
          <AppBreadcrumb
            :items="breadcrumbItems"
            :aria-label="breadcrumbAriaLabel"
            :show-back="true"
            :back-label="breadcrumbBackLabel"
            :fallback-target="{ name: 'paymentReceipts' }"
          />

          <div class="app-detail-header payment-receipt-detail-heading-wrap">
            <div class="app-detail-heading payment-receipt-detail-heading-content">
              <div class="payment-receipt-detail-heading-main">
                <h2 class="payment-receipt-detail-heading-title">{{ detailTitle }}</h2>
                <span
                  class="app-status-pill app-detail-status-pill"
                  :class="isCanceled ? 'payment-receipts-status-pill--canceled' : 'payment-receipts-status-pill--active'"
                >
                  {{ statusLabel }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #actions="{ onAction }">
        <div class="app-page-header-actions app-detail-header-actions">
          <button
            v-for="action in primaryHeaderActions"
            :key="action.key"
            class="app-header-btn"
            :class="[
              `app-header-btn--${action.variant || 'primary'}`,
              {
                'payment-receipt-detail-header-btn--icon-only': action.iconOnly,
                'payment-receipt-detail-header-btn--download-bundle': action.key === 'download',
              }
            ]"
            type="button"
            :title="String(action?.title || action?.label || '')"
            @click="handlePrimaryHeaderAction(action, onAction)"
          >
            <i v-if="action.icon" class="fa" :class="action.icon" aria-hidden="true"></i>
            <span v-if="!action.iconOnly">{{ action.label }}</span>
          </button>

          <div v-if="secondaryHeaderActions.length" class="app-detail-more-actions-wrap" ref="moreActionsWrap">
            <button
              type="button"
              class="app-header-btn app-header-btn--ghost app-detail-more-actions-btn"
              :title="text.detail.header.moreActionsTitle"
              @click.stop="toggleMoreActionsMenu"
            >
              <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>

            <div v-if="showMoreActionsMenu" class="app-detail-more-actions-menu">
              <button
                v-for="action in secondaryHeaderActions"
                :key="action.key"
                type="button"
                :class="{ 'payment-receipt-detail-more-actions-menu__item--critical': action.key === 'cancel' }"
                @click="handleSecondaryHeaderAction(action, onAction)"
              >
                <span>{{ action.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <section class="payment-receipt-detail-content-wrap">
          <AppSectionTabs
            v-model="activeTab"
            :tabs="detailTabs"
            :aria-label="sectionTabsAriaLabel"
          />

          <PaymentReceiptDetailTabsPanel
            :active-tab="activeTab"
            :payment-receipt="paymentReceiptData"
          />
        </section>
      </template>
    </PaymentReceiptsPageShell>

    <SendEmailModal
      context-type="payment_receipt"
      :context-data="sendEmailContextData"
      :show="showSendEmailModal"
      :submitting="sendEmailModalSubmitting"
      @close="closeSendEmailModal"
      @submit="submitSendEmailModal"
    />
  </div>
</template>

<script>
import AppBreadcrumb from '@/components/common/AppBreadcrumb.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import SendEmailModal from '@/components/sendEmail/SendEmailModal.vue';
import PaymentReceiptsPageShell from '@/components/paymentReceipts/PaymentReceiptsPageShell.vue';
import PaymentReceiptDetailTabsPanel from '@/components/paymentReceipts/detail/PaymentReceiptDetailTabsPanel.vue';
import paymentReceiptsService from '@/services/paymentReceipts';
import { extractApiErrorMessage, extractPayload } from '@/utils/dataNormalization';
import { downloadBlobFile } from '@/utils/fileDownload';
import { useDomainText } from '@/composables/text/useDomainText';
import { buildInvoiceDetailHeaderActions } from '@/composables/invoices/detail/helpers';
import { invoiceDetailSendEmailMethods } from '@/composables/send/sendEmailMethods';

const { catalog: paymentReceiptsText } = useDomainText('paymentReceipts');
const { catalog: sendEmailText } = useDomainText('sendEmail');

export default {
  name: 'PaymentReceiptDetailPage',
  components: {
    AppBreadcrumb,
    AppSectionTabs,
    SendEmailModal,
    PaymentReceiptsPageShell,
    PaymentReceiptDetailTabsPanel,
  },
  data() {
    return {
      text: paymentReceiptsText,
      sendEmailText,
      activeTab: 'detail',
      paymentReceiptData: null,
      showMoreActionsMenu: false,
      showSendEmailModal: false,
      sendEmailModalSubmitting: false,
    };
  },
  computed: {
    paymentReceiptId() {
      return Number(this.$route?.params?.paymentReceiptId || 0) || 0;
    },
    headerTitle() {
      return this.detailTitle;
    }, 
    detailTitle() {
      const pageTitle = String(this.text?.detail?.page?.title || 'Pago').trim();
      const folio = String(this.paymentReceiptData?.folio || '').trim();
      return folio !== '' ? `${pageTitle} ${folio}`.trim() : pageTitle;
    },
    statusLabel() {
      return String(this.paymentReceiptData?.status || '').trim() || String(this.text?.detail?.status?.active || 'VIGENTE');
    },
    isCanceled() {
      return String(this.paymentReceiptData?.status_key || '').trim().toLowerCase() === 'canceled';
    },
    isStampedPaymentReceipt() {
      return Number(this.paymentReceiptData?.is_stamped || 0) === 1;
    },
    headerActions() {
      const labels = this.text?.detail?.headerActions || {};
      const actions = buildInvoiceDetailHeaderActions(labels);
      return actions.filter((action) => {
        const key = String(action?.key || '');
        if (key === 'clone' || key === 'pay') {
          return false;
        }

        if (!this.isStampedPaymentReceipt && (key === 'download' || key === 'downloadXml')) {
          return false;
        }

        return true;
      });
    },
    primaryHeaderActions() {
      const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
      const primaryKeys = this.isStampedPaymentReceipt
        ? ['downloadXml', 'send']
        : ['downloadPdf', 'send'];

      return actions.filter((action) => primaryKeys.includes(String(action?.key || '')));
    },
    secondaryHeaderActions() {
      const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
      const primaryKeys = new Set(this.primaryHeaderActions.map((action) => String(action?.key || '')));
      const secondary = actions.filter((action) => !primaryKeys.has(String(action?.key || '')));
      const priorityByKey = {
        downloadPdf: 1,
        download: 2,
      };

      return secondary.slice().sort((left, right) => {
        const leftKey = String(left?.key || '');
        const rightKey = String(right?.key || '');
        const leftPriority = priorityByKey[leftKey] || 99;
        const rightPriority = priorityByKey[rightKey] || 99;

        if (leftPriority !== rightPriority) {
          return leftPriority - rightPriority;
        }

        return 0;
      });
    },
    detailTabs() {
      const tabLabels = this.text?.detail?.tabs || {};
      return [
        { key: 'detail', label: String(tabLabels.detail || '').trim() },
        { key: 'shipping', label: String(tabLabels.shipping || '').trim() },
        { key: 'documents', label: String(tabLabels.documents || '').trim() },
        { key: 'history', label: String(tabLabels.history || '').trim() },
      ];
    },
    sectionTabsAriaLabel() {
      return String(this.text?.detail?.sectionTabsAriaLabel || '').trim();
    },
    breadcrumbItems() {
      return [
        { label: String(this.text?.page?.title || 'Pagos').trim(), to: { name: 'paymentReceipts' } },
        { label: this.detailTitle },
      ];
    },
    breadcrumbAriaLabel() {
      return String(this.text?.detail?.breadcrumb?.ariaLabel || 'Ruta de pago').trim();
    },
    breadcrumbBackLabel() {
      return String(this.text?.detail?.breadcrumb?.backLabel || 'Volver').trim();
    },
    sendEmailContextData() {
      const paymentReceiptData = this.paymentReceiptData && typeof this.paymentReceiptData === 'object'
        ? this.paymentReceiptData
        : {};

      return {
        id: this.paymentReceiptId,
        total: paymentReceiptData?.total,
        customerId: paymentReceiptData?.customer_id,
        customerName: String(paymentReceiptData?.customer_name || paymentReceiptData?.receiver_business_name || '').trim(),
        currency: String(paymentReceiptData?.currency || '').trim(),
        paymentReceiptData,
      };
    },
  },
  created() {
    this.loadPaymentReceiptDetail();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    handlePrimaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      onAction?.(action);
    },
    handleSecondaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      onAction?.(action);
    },
    toggleMoreActionsMenu() {
      this.showMoreActionsMenu = !this.showMoreActionsMenu;
    },
    handleDocumentClick(event) {
      const target = event?.target;
      if (target && target.closest('.app-detail-more-actions-wrap')) {
        return;
      }

      this.showMoreActionsMenu = false;
    },
    onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim();

      if (actionKey === 'send') {
        this.openSendEmailModal();
        return;
      }

      if (actionKey === 'downloadPdf') {
        this.downloadPaymentReceiptPdf();
        return;
      }

      if (actionKey === 'downloadXml') {
        this.downloadPaymentReceiptXml();
        return;
      }

      if (actionKey === 'download') {
        this.downloadPaymentReceiptZip();
        return;
      }

      const actionToasts = this.text?.detail?.toast?.actions || {};
      const fallbackActionKey = actionKey === 'download' ? 'downloadPdf' : actionKey;
      const message = String(actionToasts?.[actionKey] || actionToasts?.[fallbackActionKey] || '').trim();
      if (message !== '') {
        this.$toast?.info(message);
      }
    },
    buildPaymentReceiptDocumentBaseName() {
      const folio = String(this.paymentReceiptData?.folio || '').trim();
      const id = Number(this.paymentReceiptData?.id || this.paymentReceiptId || 0) || 0;
      const fallback = id > 0 ? `pago_${id}` : 'pago';

      return (folio || fallback)
        .replace(/\s+/g, '_')
        .replace(/[^A-Za-z0-9_\-]/g, '') || fallback;
    },
    async downloadPaymentReceiptPdf() {
      if (this.paymentReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      try {
        const response = await paymentReceiptsService.downloadPdf(this.paymentReceiptId);
        this.openPdfBlob(response?.data ?? response);
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.actions?.downloadPdf || 'No fue posible descargar el PDF del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    async downloadPaymentReceiptXml() {
      if (this.paymentReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      if (!this.isStampedPaymentReceipt) {
        return;
      }

      try {
        const response = await paymentReceiptsService.downloadXml(this.paymentReceiptId);
        downloadBlobFile(response?.data ?? response, `${this.buildPaymentReceiptDocumentBaseName()}.xml`, 'application/xml');
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.actions?.downloadXml || 'No fue posible descargar el XML del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    async downloadPaymentReceiptZip() {
      if (this.paymentReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      if (!this.isStampedPaymentReceipt) {
        return;
      }

      try {
        const response = await paymentReceiptsService.downloadZip(this.paymentReceiptId);
        downloadBlobFile(response?.data ?? response, `${this.buildPaymentReceiptDocumentBaseName()}_cfdi.zip`, 'application/zip');
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.actions?.download || 'No fue posible descargar el ZIP del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    openPdfBlob(payload) {
      const blob = payload instanceof Blob
        ? payload
        : new Blob([payload], { type: 'application/pdf' });

      const objectUrl = window.URL.createObjectURL(blob);
      const opened = window.open(objectUrl, '_blank', 'noopener');

      if (!opened) {
        downloadBlobFile(blob, `${this.buildPaymentReceiptDocumentBaseName()}.pdf`, 'application/pdf');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60 * 1000);
    },
    async loadPaymentReceiptDetail() {
      if (this.paymentReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        this.$router.replace({ name: 'paymentReceipts' });
        return;
      }

      try {
        const response = await paymentReceiptsService.show(this.paymentReceiptId);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage !== '') {
          this.$toast?.error(apiErrorMessage);
          this.$router.replace({ name: 'paymentReceipts' });
          return;
        }

        const payload = extractPayload(response);
        if (payload && typeof payload === 'object' && Number(payload?.id || 0) > 0) {
          this.paymentReceiptData = payload;
          return;
        }
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.loadPaymentReceiptError || 'No fue posible cargar el detalle del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        if (String(message || '').trim() !== '') {
          this.$toast?.error(message);
        }
      }

      this.$router.replace({ name: 'paymentReceipts' });
    },
    ...invoiceDetailSendEmailMethods,
  },
};
</script>

<style scoped>
.payment-receipt-detail-header-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.payment-receipt-detail-content-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-receipt-detail-heading-wrap {
  align-items: center;
}

.payment-receipt-detail-heading-content {
  width: 100%;
}

.payment-receipt-detail-heading-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.payment-receipt-detail-heading-title {
  margin: 0;
}

.payment-receipt-detail-more-actions-menu__item--critical {
  color: #b91c1c;
}

.payment-receipt-detail-more-actions-menu__item--critical:hover {
  background: rgba(185, 28, 28, 0.08);
  color: #991b1b;
}

.payment-receipts-status-pill--active {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.payment-receipts-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}

.payment-receipt-detail-header-btn--icon-only {
  min-width: 36px;
  padding-left: 10px;
  padding-right: 10px;
}

.payment-receipt-detail-header-btn--download-bundle {
  background: rgba(31, 42, 61, 0.1);
  color: #1f2a3d;
  border: 1px solid rgba(31, 42, 61, 0.2);
}

.payment-receipt-detail-header-btn--download-bundle:not(:disabled):hover {
  background: rgba(31, 42, 61, 0.16);
}
</style>
